/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import GeneralImage, { IGeneralImageProps } from './GeneralImage';
import MagnifyingGlassPlusLight from '@/assets/magnifyingGlassPlusLight.svg';
import ArrowUUpLeft from '@/assets/arrowUUpLeft.svg';

import { useTranslations } from 'next-intl';
import TapDetector from '@/utils/tap-detector';
import _debounce from '@/utils/debounce';
import Tooltip from '../general/Tooltip';
import { useFullscreen } from '@/utils';

interface IGeneralImageZoomProps extends IGeneralImageProps {
  maxScale?: number;
  minScale?: number;
  pivot?: 'cursor' | 'image-center';
  zoomingElastic?: boolean;
  limitTranslation?: boolean;
  mouseWheelToZoom?: boolean;
  visible?: boolean;
  zoomActivated?: () => void;
  zoomDeactivated?: () => void;
}

const GeneralImageZoom = ({
  zoomActivated,
  zoomDeactivated,
  maxScale = 9,
  minScale = 1,
  pivot = 'cursor',
  zoomingElastic = true,
  limitTranslation = true,
  mouseWheelToZoom = true,
  visible,
  buttons,
  className,
  ...props
}: IGeneralImageZoomProps) => {
  const [zoomActive, setZoomActive] = useState(false);
  // Container sizes, used to determin the initial zoomer size.
  // Need to reactive to window resizing.
  const [containerWidth, setContainerWidth] = useState<number>(1);
  const containerWidthRef = useRef<number>(1);
  const [containerHeight, setContainerHeight] = useState<number>(1);
  const containerHeightRef = useRef<number>(1);
  const containerLeft = useRef<number>(0);
  const containerTop = useRef<number>(0);
  const aspectRatio = useRef<number>(1);
  // Store values: Interactions will at last change these values.
  // After rotation or resize, these values will keep still.
  // These three values are all relative to the container size.
  const translateX = useRef<number>(0);
  const [animTranslateX, setAnimTranslateX] = useState<number>(0);
  const animTranslateXRef = useRef<number>(0);
  const translateY = useRef<number>(0);
  const [animTranslateY, setAnimTranslateY] = useState<number>(0);
  const animTranslateYRef = useRef<number>(0);
  const [scale, setScale] = useState<number>(1);
  const scaleRef = useRef<number>(1);
  const [animScale, setAnimScale] = useState<number>(1);
  const animScaleRef = useRef(1);
  // Mouse states
  const lastFullWheelTime = useRef<number>(0);
  const lastWheelTime = useRef<number>(0);
  const lastWheelDirection = useRef<'y' | 'x'>('y');
  const [isPointerDown, setIsPointerDown] = useState<boolean>(false);
  const isPointerDownRef = useRef<boolean>(false);
  const pointerPosX = useRef<number>(-1);
  const pointerPosY = useRef<number>(-1);
  const twoFingerInitDist = useRef<number>(0);
  const panLocked = useRef<boolean>(true);

  const t = useTranslations();
  const generalImage = useRef<HTMLDivElement>(null);

  const raf = useRef<number>(null);
  const tapDetector = useRef<TapDetector>(null);
  const elRef = useRef<HTMLDivElement>(null);

  const { isFullscreen } = useFullscreen();

  useEffect(() => {
    if ((!visible || !isFullscreen) && zoomActive) {
      deactivateZoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isFullscreen]);

  useEffect(() => {
    if (scale !== 1) {
      panLocked.current = false;
    }
  }, [scale]);

  useEffect(() => {
    return () => {
      deactivateZoom();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activateZoom = () => {
    const generalImageElement = generalImage.current;
    if (generalImageElement) {
      setZoomActive(true);
      zoomActivated?.();
      zoomIn();
      tapDetector.current = new TapDetector();
      tapDetector.current.attach(elRef.current);
      tapDetector.current.onSingleTap(onSingleTap);

      window.addEventListener('resize', onWindowResize);
      onWindowResize();
      refreshContainerPos();
      loop();
      generalImageElement.addEventListener('mousewheel', onMouseWheel);
      generalImageElement.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mouseup', onMouseUp);
      generalImageElement.addEventListener('mousemove', onMouseMove);
      generalImageElement.addEventListener('mouseout', setPointerPosCenter);
      generalImageElement.addEventListener('touchstart', onTouchStart);
      generalImageElement.addEventListener('touchend', onTouchEnd);
      generalImageElement.addEventListener('touchmove', onTouchMove);
      document.addEventListener('gesturestart', onGestureStart);
    }
  };

  const deactivateZoom = () => {
    setZoomActive(false);

    window.removeEventListener('mouseup', onMouseUp);
    reset();

    window.removeEventListener('resize', onWindowResize);
    window.cancelAnimationFrame(raf.current ?? 0);
    document.removeEventListener('gesturestart', onGestureStart);
    zoomDeactivated?.();

    const generalImageElement = generalImage.current ? generalImage.current : null;
    if (generalImageElement) {
      generalImageElement.removeEventListener('mousewheel', onMouseWheel);
      generalImageElement.removeEventListener('mousedown', onMouseDown);

      generalImageElement.removeEventListener('mousemove', onMouseMove);
      generalImageElement.removeEventListener('mouseout', setPointerPosCenter);
      generalImageElement.removeEventListener('touchstart', onTouchStart);
      generalImageElement.removeEventListener('touchend', onTouchEnd);
      generalImageElement.removeEventListener('touchmove', onTouchMove);

      if (tapDetector.current) {
        tapDetector.current.detach(elRef.current);
      }
    }
  };

  const reset = () => {
    setScale(1);
    scaleRef.current = 1;
    panLocked.current = true;
    translateX.current = 0;
    translateY.current = 0;
    setAnimTranslateX(0);
    animTranslateXRef.current = 0;
    setAnimTranslateY(0);
    animTranslateYRef.current = 0;
    setAnimScale(1);
    animScaleRef.current = 1;
  };

  const zoomIn = (scale: number = 2) => {
    tryToScale(scale);
    onInteractionEnd();
  };
  const zoomOut = (scale: number = 0.5) => {
    tryToScale(scale);
    onInteractionEnd();
  };

  // Main Logic --------------------------------------------------------------
  // scale
  // Zoom the image with the point at the pointer(mouse or pinch center) pinned.
  // Simplify: This can be regard as vector pointer to old-image-center scaling.
  const tryToScale = (scaleDelta: number) => {
    let newScale = scaleRef.current * scaleDelta;
    if (zoomingElastic) {
      // damping
      if (newScale < minScale || newScale > maxScale) {
        let log = Math.log2(scaleDelta);
        log *= 0.2;
        scaleDelta = Math.pow(2, log);
        newScale = scaleRef.current * scaleDelta;
      }
    } else {
      if (newScale < minScale) newScale = minScale;
      else if (newScale > maxScale) newScale = maxScale;
    }
    scaleDelta = newScale / scaleRef.current;
    setScale(newScale);
    scaleRef.current = newScale;
    if (pivot !== 'image-center') {
      const normMousePosX = (pointerPosX.current - containerLeft.current) / containerWidthRef.current;
      const normMousePosY = (pointerPosY.current - containerTop.current) / containerHeightRef.current;
      translateX.current = (0.5 + translateX.current - normMousePosX) * scaleDelta + normMousePosX - 0.5;
      translateY.current = (0.5 + translateY.current - normMousePosY) * scaleDelta + normMousePosY - 0.5;
    }
  };

  const setPointerPosCenter = () => {
    pointerPosX.current = containerLeft.current + containerWidthRef.current / 2;
    pointerPosX.current = containerTop.current + containerHeightRef.current / 2;
  };

  // pan
  const onPointerMove = (newMousePosX: number, newMousePosY: number) => {
    if (isPointerDownRef.current) {
      const pixelDeltaX = newMousePosX - pointerPosX.current;
      const pixelDeltaY = newMousePosY - pointerPosY.current;
      if (!panLocked.current) {
        translateX.current = translateX.current + pixelDeltaX / containerWidthRef.current;
        translateY.current = translateY.current + pixelDeltaY / containerHeightRef.current;
      }
    }
    pointerPosX.current = newMousePosX;
    pointerPosY.current = newMousePosY;
  };

  const onInteractionEnd = _debounce(() => {
    limit();
    panLocked.current = scaleRef.current === 1;
  }, 100);

  const limit = () => {
    // scale
    if (scaleRef.current < minScale) {
      setScale(minScale);
      scaleRef.current = minScale;
      // FIXME this sometimes will not reset when pinching in
      // this.tryToScale(this.minScale / this.scale)
    } else if (scaleRef.current > maxScale) {
      tryToScale(maxScale / scaleRef.current);
    }
    // translate
    if (limitTranslation) {
      const translateLimit = calcTranslateLimit();
      if (Math.abs(translateX.current) > translateLimit.x) {
        translateX.current = (translateX.current * translateLimit.x) / Math.abs(translateX.current);
      }
      if (Math.abs(translateY.current) > translateLimit.y) {
        translateY.current = (translateY.current * translateLimit.y) / Math.abs(translateY.current);
      }
    }
  };

  const calcTranslateLimit = () => {
    if (getMarginDirection() === 'y') {
      const imageToContainerRatio = containerWidthRef.current / aspectRatio.current / containerHeightRef.current;
      let translateLimitY = (scaleRef.current * imageToContainerRatio - 1) / 2;
      if (translateLimitY < 0) translateLimitY = 0;
      return {
        x: (scaleRef.current - 1) / 2,
        y: translateLimitY,
      };
    } else {
      const imageToContainerRatio = (containerHeightRef.current * aspectRatio.current) / containerWidthRef.current;
      let translateLimitX = (scaleRef.current * imageToContainerRatio - 1) / 2;
      if (translateLimitX < 0) translateLimitX = 0;
      return {
        x: translateLimitX,
        y: (scaleRef.current - 1) / 2,
      };
    }
  };

  const getMarginDirection = () => {
    const containerRatio = containerWidthRef.current / containerHeightRef.current;
    return containerRatio > aspectRatio.current ? 'x' : 'y';
  };

  const onSingleTap = (ev: MouseEvent) => {
    if (scaleRef.current < maxScale) {
      if (ev.clientX > 0) {
        pointerPosX.current = ev.clientX;
        pointerPosY.current = ev.clientY;
      }
      tryToScale(Math.min(Math.ceil(scaleRef.current + 3), maxScale));
    }
    onInteractionEnd();
  };

  const onWindowResize = () => {
    if (!elRef.current) return;
    const styles = window.getComputedStyle(elRef.current);
    setContainerWidth(parseFloat(styles.width));
    containerWidthRef.current = parseFloat(styles.width);
    setContainerHeight(parseFloat(styles.height));
    containerHeightRef.current = parseFloat(styles.height);
    const generalImageElement = generalImage.current ? generalImage.current : null;

    if (generalImageElement) {
      aspectRatio.current = generalImageElement.clientWidth / generalImageElement.clientHeight;
    }
    setPointerPosCenter();
    limit();
  };

  const refreshContainerPos = () => {
    if (!elRef.current) return;
    const rect = elRef.current.getBoundingClientRect();
    containerLeft.current = rect.left;
    containerTop.current = rect.top;
  };

  const loop = () => {
    const animScaleValue = gainOn(animScaleRef.current, scaleRef.current);
    setAnimScale(animScaleValue);
    animScaleRef.current = animScaleValue;
    const animTranslateXValue = gainOn(animTranslateXRef.current, translateX.current);
    setAnimTranslateX(animTranslateXValue);
    animTranslateXRef.current = animTranslateXValue;
    const animTranslateYValue = gainOn(animTranslateYRef.current, translateY.current);
    setAnimTranslateY(animTranslateYValue);
    animTranslateYRef.current = animTranslateYValue;

    raf.current = window.requestAnimationFrame(loop);
    // console.log('loop', this.raf)
  };
  const gainOn = (from: number, to: number) => {
    const delta = (to - from) * 0.3;
    // console.log('gainOn', from, to, from + delta)
    if (Math.abs(delta) > 1e-5) {
      return from + delta;
    } else {
      return to;
    }
  };

  // Mouse Events ------------------------------------------------------------
  // Mouse wheel scroll,  TrackPad pinch or TrackPad scroll
  const onMouseWheel = (ev: any) => {
    if (!mouseWheelToZoom) return;
    // prevent is used to stop the page scroll elastic effects
    ev.preventDefault();
    if (ev.detail) ev.wheelDelta = ev.detail * -10;
    const currTime = Date.now();
    if (Math.abs(ev.wheelDelta) === 120) {
      // Throttle the TouchPad pinch on Mac, or it will be too sensitive
      if (currTime - lastFullWheelTime.current > 50) {
        onMouseWheelDo(ev.wheelDelta);
        lastFullWheelTime.current = currTime;
      }
    } else {
      if (currTime - lastWheelTime.current > 50 && typeof ev.deltaX === 'number') {
        lastWheelDirection.current = ev.detail == 0 && Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? 'x' : 'y';
      }
      if (lastWheelDirection.current === 'y') {
        onMouseWheelDo(ev.wheelDelta);
      }
    }
    lastWheelTime.current = currTime;
  };

  const onMouseWheelDo = (wheelDelta: number) => {
    // Value basis: One mouse wheel (wheelDelta=+-120) means 1.25/0.8 scale.
    const scaleDelta = Math.pow(1.25, wheelDelta / 120);
    tryToScale(scaleDelta);
    onInteractionEnd();
  };

  const onMouseDown = (ev: any) => {
    refreshContainerPos();
    setIsPointerDown(true);
    isPointerDownRef.current = true;
    // Open the context menu then click other place will skip the mousemove events.
    // This will cause the pointerPosX/Y NOT sync, then we will need to fix it on mousedown event.
    pointerPosX.current = ev.clientX;
    pointerPosY.current = ev.clientY;
    // console.log('onMouseDown', ev)
  };

  const onMouseUp = () => {
    setIsPointerDown(false);
    isPointerDownRef.current = false;
    onInteractionEnd();
  };

  const onMouseMove = (ev: any) => {
    onPointerMove(ev.clientX, ev.clientY);
    // console.log('onMouseMove client, offset', ev.clientX, ev.clientY)
  };

  // Touch Events ------------------------------------------------------------
  const onTouchStart = (ev: TouchEvent) => {
    if (ev.touches.length === 1) {
      refreshContainerPos();
      pointerPosX.current = ev.touches[0].clientX;
      pointerPosY.current = ev.touches[0].clientY;
      setIsPointerDown(true);
      isPointerDownRef.current = true;
    } else if (ev.touches.length === 2) {
      setIsPointerDown(true);
      isPointerDownRef.current = true;
      // pos
      pointerPosX.current = (ev.touches[0].clientX + ev.touches[1].clientX) / 2;
      pointerPosY.current = (ev.touches[0].clientY + ev.touches[1].clientY) / 2;
      // dist

      const distX = ev.touches[0].clientX - ev.touches[1].clientX;
      const distY = ev.touches[0].clientY - ev.touches[1].clientY;
      twoFingerInitDist.current = Math.sqrt(distX * distX + distY * distY);
    }
    // console.log('onTouchStart', ev.touches)
  };
  const onTouchEnd = (ev: TouchEvent) => {
    if (ev.touches.length === 0) {
      setIsPointerDown(false);
      isPointerDownRef.current = false;
      // Near 1 to set 1
      if (Math.abs(scaleRef.current - 1) < 0.1) {
        setScale(1);
        scaleRef.current = 1;
      }
      onInteractionEnd();
    } else if (ev.touches.length === 1) {
      pointerPosX.current = ev.touches[0].clientX;
      pointerPosY.current = ev.touches[0].clientY;
    }
    // console.log('onTouchEnd', ev.touches.length)
  };
  const onTouchMove = (ev: TouchEvent) => {
    if (ev.touches.length === 1) {
      onPointerMove(ev.touches[0].clientX, ev.touches[0].clientY);
    } else if (ev.touches.length === 2) {
      // pos
      const newMousePosX = (ev.touches[0].clientX + ev.touches[1].clientX) / 2;
      const newMousePosY = (ev.touches[0].clientY + ev.touches[1].clientY) / 2;
      onPointerMove(newMousePosX, newMousePosY);
      pointerPosX.current = newMousePosX;
      pointerPosY.current = newMousePosY;
      // dist
      const distX = ev.touches[0].clientX - ev.touches[1].clientX;
      const distY = ev.touches[0].clientY - ev.touches[1].clientY;
      const newTwoFingerDist = Math.sqrt(distX * distX + distY * distY);
      tryToScale(newTwoFingerDist / twoFingerInitDist.current);
      twoFingerInitDist.current = newTwoFingerDist;
    }
    // console.log('onTouchMove', this.pointerPosX, this.pointerPosY)
  };
  const onGestureStart = (ev: any) => {
    // Prevent the default zooming behavior of iOS Safari
    ev.preventDefault();
    // console.log('onGestureStart', ev)
  };

  return (
    <div
      ref={elRef}
      className={classNames(
        'image-zoom relative flex h-full w-full items-center justify-center',
        isPointerDown
          ? 'cursor-all-scroll'
          : zoomActive
            ? scale >= maxScale
              ? 'cursor-zoom-out'
              : 'cursor-zoom-in'
            : null,
      )}
    >
      <GeneralImage
        ref={generalImage}
        buttons={[
          {
            icon: <MagnifyingGlassPlusLight className="h-6 w-6 !fill-black" />,
            tooltipText: t('imageGallery.$zoomIn'),
            onClick: activateZoom,
          },
          ...(buttons || []),
        ]}
        style={{
          transform: `translate(${containerWidth * animTranslateX}px, ${containerHeight * animTranslateY}px) scale(${animScale})`,
        }}
        className={classNames(
          className,
          zoomActive &&
            '[&_img]:user-drag-none [&_.buttons]:hidden [&_.caption]:hidden [&_.copyright]:hidden [&_.general-image]:mb-0',
        )}
        {...props}
      />
      {zoomActive && (
        <div className="absolute top-3">
          <Tooltip placement="bottom" content={t('imageGallery.$exitZoom')}>
            <button
              className="btn icon cursor-pointer rounded-full p-2 shadow-md transition-all"
              onClick={deactivateZoom}
            >
              <ArrowUUpLeft className="h-6 w-6 rotate-180 !fill-black" />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default GeneralImageZoom;
